import type { ReactNode } from 'react'
import { Info } from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  cn,
} from '@atrvd/saas-ui'
import { formatCurrency } from '@dcp/shared/utils/formatters'
import { DataWarningIcon } from './DataWarningIcon'

interface BalanceTileRowProps {
  children: ReactNode
}

/**
 * Grid wrapper for a row of aggregated-balance tiles.
 *
 * The five-across tier starts at `xl`, not `lg`: the app shell eats 256px of
 * sidebar plus 48px of page padding, so at a 1024px viewport five columns leave
 * 97px of inner width per tile. Measured in Chromium against the compiled
 * stylesheet with the figures from the DCP-297 report ($1,454,306, whole
 * dollars): they overflow the card by 30px at 1024px and by 4px at 1152px, and
 * the label needs a third line. Five across only becomes safe at 1280px
 * (148px inner).
 *
 * Those widths come from a 0.633em/digit fallback font — note that the `Inter`
 * declared in tailwind.config.js is never actually loaded, so the app renders
 * in the OS system font (~0.60em/digit, i.e. ~5% narrower). That only makes the
 * thresholds conservative: even at the narrower advance an 8-digit value still
 * overflows a five-across tile below 1280px.
 *
 * DCP-392: values now render with 2 decimals instead of whole dollars, i.e. 3
 * more glyphs per tile (".XX") than the DCP-297 measurements above assumed, so
 * the row was re-measured in Chromium against the compiled stylesheet inside
 * this shell. Inner width per tile: 148px at 1280px, 166px at 1366px, 180px at
 * 1440px, 200px at 1536px and 276px at 1920px (five across), and 195px at
 * 1024px / 238px at 1152px (three across — the DCP-297 figures above are for a
 * hypothetical five across at those widths, which is exactly why the tier
 * starts at `xl`). With cents, $4,886,415.00 needs 162.0px at `text-xl` and so
 * overflowed the 1280px tile by 14px — which is why `BalanceTile` steps the
 * value down to `text-lg` on the `xl` tier alone. The three-across widths keep
 * `text-xl`: they have 33-76px of slack for that figure and never overflowed.
 *
 * @param children - `BalanceTile` elements to lay out.
 */
export function BalanceTileRow({ children }: BalanceTileRowProps) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
      {children}
    </div>
  )
}

export interface BalanceTileProps {
  /** Metric name shown in the card header. */
  label: string
  /**
   * Amount in dollars; rendered with cents (2 decimals), negatives keep a
   * leading minus. `null` means the figure could not be loaded and renders as
   * an em dash, so it is never mistaken for a real $0.
   */
  value: number | null
  /** Optional colour class for the value (e.g. `text-red-600`). */
  valueClassName?: string
  /** Explanation of how the metric is derived, shown on the Info icon. */
  tooltip?: string
  /** "Data missing/stale for today" note, shown on a warning icon (DCP-218 AC.3). */
  warning?: string
  /** Replaces the value with a skeleton; the label stays visible. */
  loading?: boolean
}

/**
 * A single aggregated-balance tile.
 *
 * DCP-297 — the geometry is what keeps the values of a row on one bottom edge:
 * grid items stretch to the row height, `flex flex-col` makes that height
 * distributable, and `mt-auto` on the content pushes the value to the bottom.
 * That holds for any number of label lines, so a wrapped label ("Book vs.
 * Intacct Variance") does not drop its value below its neighbours'. `min-h-10`
 * only reserves two label lines so a lone tile in a one-column layout does not
 * look squashed — it is not what produces the alignment. `leading-5` is
 * load-bearing though: CardTitle's base class carries `leading-none`, which
 * would otherwise make the two-line arithmetic unpredictable.
 *
 * The label is never skeletonised — it is static copy, so rendering it during
 * loading makes the loading and loaded geometry identical by construction.
 */
export function BalanceTile({
  label,
  value,
  valueClassName,
  tooltip,
  warning,
  loading,
}: BalanceTileProps) {
  return (
    <Card className="flex h-full flex-col">
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 p-4 pb-2">
        <CardTitle className="min-h-10 text-sm font-medium leading-5 text-muted-foreground">
          {label}
        </CardTitle>
        {/* items-start above + mt-0.5 here keeps the icons on the label's first
            line; shrink-0 stops them being squeezed at the 5-across tier. */}
        <div className="mt-0.5 flex shrink-0 items-center gap-1">
          {warning && <DataWarningIcon message={warning} />}
          {tooltip && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 cursor-help text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p>{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </CardHeader>
      <CardContent className="mt-auto p-4 pt-0">
        {loading ? (
          // h-7/h-8 track the value's line box exactly: text-xl and text-lg
          // both have a 28px line box (so the DCP-392 step down on the `xl`
          // tier needs no second skeleton height), text-2xl has 32px. The row
          // therefore does not shift when data lands.
          <Skeleton className="h-7 w-24 2xl:h-8" />
        ) : (
          // tabular-nums + lining-nums: constant digit advance, so the five
          // figures read as a comparable set and the row does not reflow
          // between refreshes (matches CashPositionSummaryGrid).
          // Three-step scale (DCP-392): text-xl below `xl`, text-lg on the
          // `xl` tier only, text-2xl from `2xl`. The step DOWN in the middle is
          // deliberate and is the narrowest fix for the overflow that came with
          // cents: `xl` is where the grid goes five across, and there each tile
          // has just 148px of inner width at a 1280px viewport, while
          // $4,886,415.00 measures 162.0px at text-xl — it overflowed by 14px.
          // At text-lg the same value is 145.8px and fits (2.2px of slack at
          // 1280px, 20.2px at 1366px, 34.2px at 1440px).
          // Below `xl` the grid is three across (195px inner at 1024px, 238px
          // at 1152px), which never overflowed, so those widths keep the larger
          // text-xl: measured slack there is 33.1px at 1024px / 76.1px at
          // 1152px for the same figure.
          // text-lg and text-xl share a 28px line box, so the Skeleton `h-7`
          // and the bottom-alignment geometry are untouched across the step
          // (measured: 0px delta between the skeleton and the value at every
          // width, and all five values on one bottom edge at 1024-1920px,
          // loaded and loading).
          // Known and accepted: an 8-digit value still overflows, at 1280px
          // ($12,345,678.90 = 158.3px at text-lg vs 148px) and at 1536px where
          // text-2xl takes over (211.1px vs 200px). Nothing else overflows at
          // any tested width.
          // That residual is cosmetic, not a loss of digits, and the distinction
          // was measured rather than assumed: the overflow is of the CONTENT box
          // only, and `p-4` + the border leave 17px of buffer, so the glyphs stop
          // 7.1px INSIDE the card at 1280px (5.5px at 1536px). No ancestor up to
          // `main` clips on X — Card carries no overflow-hidden — and `main`'s
          // scrollWidth stays equal to its clientWidth, so there is no clipping
          // and no horizontal scrollbar; the figure only sits optically tight
          // against the card's right edge. The frame genuinely breaks much later:
          // 9 digits (>= $100M) cross the card edge, and 10 digits (>= $1B, or a
          // negative >= $100M at 1536px) reach the neighbouring card. Fixing the
          // 8-digit case would need text-base at `xl` AND text-lg at `2xl` — two
          // tiers smaller for every customer, plus two Skeleton heights, because
          // text-base has a 24px line box instead of 28px and would break the
          // DCP-297 skeleton match. Not worth 10px of padding; revisit with a
          // magnitude-aware format if $100M+ balances ever appear.
          // All figures are Chromium measurements against the compiled
          // stylesheet in this shell, rendered in the DejaVu Sans system
          // fallback — the widest realistic system font, so a client on
          // Segoe UI/SF only gains slack.
          <div
            className={cn(
              'text-xl font-bold tabular-nums lining-nums xl:text-lg 2xl:text-2xl',
              // An unknown value is not a balance: neutral, not the metric's colour.
              value === null ? 'text-muted-foreground' : valueClassName
            )}
          >
            {/* Normalise negative zero: `useBankingKpi` builds Outstanding
                Checks as `-outstandingAmount`, so a facility with no
                outstanding checks yields JS -0, which Intl renders as "-$0".
                `value === 0` is true for -0, so this maps it back to plain 0. */}
            {value === null ? (
              <>
                <span aria-hidden="true">—</span>
                <span className="sr-only">Not available</span>
              </>
            ) : (
              formatCurrency(value === 0 ? 0 : value)
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
